import React, { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../App";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { toast } from "sonner";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Plus, Edit, Trash2, Award, Gift, Users, TrendingUp } from "lucide-react";

const AdminGamification = () => {
  const [badges, setBadges] = useState([]);
  const [rewards, setRewards] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showBadgeModal, setShowBadgeModal] = useState(false);
  const [showRewardModal, setShowRewardModal] = useState(false);
  const [showAwardModal, setShowAwardModal] = useState(false);
  const [editingBadge, setEditingBadge] = useState(null);
  const [editingReward, setEditingReward] = useState(null);
  const [selectedBadgeToAward, setSelectedBadgeToAward] = useState(null);
  const [selectedUser, setSelectedUser] = useState("");
  const token = localStorage.getItem("token");

  const [badgeForm, setBadgeForm] = useState({
    name: "",
    description: "",
    icon: "🏆",
    category: "assessment",
    requirement: "",
    points_required: 0,
    color: "blue",
    rarity: "common"
  });

  const [rewardForm, setRewardForm] = useState({
    title: "",
    description: "",
    cost_points: 0,
    reward_type: "consultation_discount",
    value: "",
    available: true,
    quantity: null,
    icon: "🎁"
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [badgesRes, rewardsRes, usersRes] = await Promise.all([
        axios.get(`${API}/badges`),
        axios.get(`${API}/rewards`),
        axios.get(`${API}/admin/users`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      setBadges(badgesRes.data);
      setRewards(rewardsRes.data);
      setUsers(usersRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const openBadgeModal = (badge = null) => {
    if (badge) {
      setBadgeForm(badge);
      setEditingBadge(badge.id);
    } else {
      setBadgeForm({
        name: "",
        description: "",
        icon: "🏆",
        category: "assessment",
        requirement: "",
        points_required: 0,
        color: "blue",
        rarity: "common"
      });
      setEditingBadge(null);
    }
    setShowBadgeModal(true);
  };

  const openRewardModal = (reward = null) => {
    if (reward) {
      setRewardForm(reward);
      setEditingReward(reward.id);
    } else {
      setRewardForm({
        title: "",
        description: "",
        cost_points: 0,
        reward_type: "consultation_discount",
        value: "",
        available: true,
        quantity: null,
        icon: "🎁"
      });
      setEditingReward(null);
    }
    setShowRewardModal(true);
  };

  const saveBadge = async () => {
    try {
      if (editingBadge) {
        await axios.put(
          `${API}/admin/badges/${editingBadge}`,
          badgeForm,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success("Badge updated successfully");
      } else {
        await axios.post(
          `${API}/admin/badges`,
          badgeForm,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success("Badge created successfully");
      }
      setShowBadgeModal(false);
      fetchData();
    } catch (error) {
      console.error("Error saving badge:", error);
      toast.error("Failed to save badge");
    }
  };

  const saveReward = async () => {
    try {
      if (editingReward) {
        await axios.patch(
          `${API}/admin/rewards/${editingReward}`,
          rewardForm,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success("Reward updated successfully");
      } else {
        await axios.post(
          `${API}/admin/rewards`,
          rewardForm,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success("Reward created successfully");
      }
      setShowRewardModal(false);
      fetchData();
    } catch (error) {
      console.error("Error saving reward:", error);
      toast.error("Failed to save reward");
    }
  };

  const deleteBadge = async (badgeId) => {
    if (!window.confirm("Are you sure you want to delete this badge?")) return;
    
    try {
      await axios.delete(
        `${API}/admin/badges/${badgeId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Badge deleted successfully");
      fetchData();
    } catch (error) {
      console.error("Error deleting badge:", error);
      toast.error("Failed to delete badge");
    }
  };

  const deleteReward = async (rewardId) => {
    if (!window.confirm("Are you sure you want to delete this reward?")) return;
    
    try {
      await axios.delete(
        `${API}/admin/rewards/${rewardId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Reward deleted successfully");
      fetchData();
    } catch (error) {
      console.error("Error deleting reward:", error);
      toast.error("Failed to delete reward");
    }
  };

  const awardBadge = async () => {
    if (!selectedUser || !selectedBadgeToAward) {
      toast.error("Please select both a user and a badge");
      return;
    }

    try {
      await axios.post(
        `${API}/badges/${selectedBadgeToAward}/award`,
        { user_id: selectedUser },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Badge awarded successfully");
      setShowAwardModal(false);
      setSelectedUser("");
      setSelectedBadgeToAward(null);
    } catch (error) {
      console.error("Error awarding badge:", error);
      toast.error(error.response?.data?.detail || "Failed to award badge");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="container mx-auto px-4 py-8">
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <div className="container mx-auto px-4 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Gamification Management</h1>
          <p className="text-gray-600">Manage badges, rewards, and gamification features</p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total Badges</p>
                  <p className="text-2xl font-bold">{badges.length}</p>
                </div>
                <Award className="w-8 h-8 text-blue-500" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total Rewards</p>
                  <p className="text-2xl font-bold">{rewards.length}</p>
                </div>
                <Gift className="w-8 h-8 text-purple-500" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Active Users</p>
                  <p className="text-2xl font-bold">{users.length}</p>
                </div>
                <Users className="w-8 h-8 text-green-500" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Available Rewards</p>
                  <p className="text-2xl font-bold">{rewards.filter(r => r.available).length}</p>
                </div>
                <TrendingUp className="w-8 h-8 text-orange-500" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="badges">
          <TabsList className="mb-6">
            <TabsTrigger value="badges">Badges</TabsTrigger>
            <TabsTrigger value="rewards">Rewards</TabsTrigger>
            <TabsTrigger value="award">Award Badges</TabsTrigger>
          </TabsList>

          {/* Badges Tab */}
          <TabsContent value="badges">
            <div className="mb-4 flex justify-between items-center">
              <h2 className="text-xl font-semibold">Badge Management</h2>
              <Button onClick={() => openBadgeModal()}>
                <Plus className="w-4 h-4 mr-2" />
                Create Badge
              </Button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {badges.map((badge) => (
                <Card key={badge.id}>
                  <CardContent className="pt-6">
                    <div className="flex items-start justify-between mb-3">
                      <span className="text-3xl">{badge.icon}</span>
                      <div className="flex gap-2">
                        <Button size="sm" variant="outline" onClick={() => openBadgeModal(badge)}>
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button size="sm" variant="destructive" onClick={() => deleteBadge(badge.id)}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                    <h3 className="font-bold mb-1">{badge.name}</h3>
                    <p className="text-sm text-gray-600 mb-2">{badge.description}</p>
                    <div className="flex gap-2">
                      <Badge variant="outline">{badge.category}</Badge>
                      <Badge variant="outline">{badge.rarity}</Badge>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          {/* Rewards Tab */}
          <TabsContent value="rewards">
            <div className="mb-4 flex justify-between items-center">
              <h2 className="text-xl font-semibold">Reward Management</h2>
              <Button onClick={() => openRewardModal()}>
                <Plus className="w-4 h-4 mr-2" />
                Create Reward
              </Button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {rewards.map((reward) => (
                <Card key={reward.id}>
                  <CardContent className="pt-6">
                    <div className="flex items-start justify-between mb-3">
                      <span className="text-3xl">{reward.icon}</span>
                      <div className="flex gap-2">
                        <Button size="sm" variant="outline" onClick={() => openRewardModal(reward)}>
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button size="sm" variant="destructive" onClick={() => deleteReward(reward.id)}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                    <h3 className="font-bold mb-1">{reward.title}</h3>
                    <p className="text-sm text-gray-600 mb-2">{reward.description}</p>
                    <div className="flex items-center justify-between">
                      <Badge variant="outline">{reward.cost_points} points</Badge>
                      <Badge variant={reward.available ? "default" : "secondary"}>
                        {reward.available ? "Available" : "Unavailable"}
                      </Badge>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          {/* Award Badges Tab */}
          <TabsContent value="award">
            <Card>
              <CardHeader>
                <CardTitle>Award Badge to User</CardTitle>
                <CardDescription>Select a user and badge to award manually</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>Select User</Label>
                  <Select value={selectedUser} onValueChange={setSelectedUser}>
                    <SelectTrigger>
                      <SelectValue placeholder="Choose a user..." />
                    </SelectTrigger>
                    <SelectContent>
                      {users.map((user) => (
                        <SelectItem key={user.id} value={user.id}>
                          {user.name} ({user.email})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Select Badge</Label>
                  <Select value={selectedBadgeToAward} onValueChange={setSelectedBadgeToAward}>
                    <SelectTrigger>
                      <SelectValue placeholder="Choose a badge..." />
                    </SelectTrigger>
                    <SelectContent>
                      {badges.map((badge) => (
                        <SelectItem key={badge.id} value={badge.id}>
                          {badge.icon} {badge.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button onClick={awardBadge} disabled={!selectedUser || !selectedBadgeToAward}>
                  Award Badge
                </Button>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Badge Modal */}
        <Dialog open={showBadgeModal} onOpenChange={setShowBadgeModal}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>{editingBadge ? "Edit Badge" : "Create Badge"}</DialogTitle>
            </DialogHeader>
            <div className="grid grid-cols-2 gap-4 py-4">
              <div>
                <Label>Name</Label>
                <Input value={badgeForm.name} onChange={(e) => setBadgeForm({...badgeForm, name: e.target.value})} />
              </div>
              <div>
                <Label>Icon (Emoji)</Label>
                <Input value={badgeForm.icon} onChange={(e) => setBadgeForm({...badgeForm, icon: e.target.value})} />
              </div>
              <div className="col-span-2">
                <Label>Description</Label>
                <Textarea value={badgeForm.description} onChange={(e) => setBadgeForm({...badgeForm, description: e.target.value})} />
              </div>
              <div>
                <Label>Category</Label>
                <Select value={badgeForm.category} onValueChange={(value) => setBadgeForm({...badgeForm, category: value})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="assessment">Assessment</SelectItem>
                    <SelectItem value="social">Social</SelectItem>
                    <SelectItem value="learning">Learning</SelectItem>
                    <SelectItem value="achievement">Achievement</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Rarity</Label>
                <Select value={badgeForm.rarity} onValueChange={(value) => setBadgeForm({...badgeForm, rarity: value})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="common">Common</SelectItem>
                    <SelectItem value="rare">Rare</SelectItem>
                    <SelectItem value="epic">Epic</SelectItem>
                    <SelectItem value="legendary">Legendary</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2">
                <Label>Requirement</Label>
                <Textarea value={badgeForm.requirement} onChange={(e) => setBadgeForm({...badgeForm, requirement: e.target.value})} />
              </div>
              <div>
                <Label>Points Required</Label>
                <Input type="number" value={badgeForm.points_required} onChange={(e) => setBadgeForm({...badgeForm, points_required: parseInt(e.target.value) || 0})} />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowBadgeModal(false)}>Cancel</Button>
              <Button onClick={saveBadge}>{editingBadge ? "Update" : "Create"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Reward Modal */}
        <Dialog open={showRewardModal} onOpenChange={setShowRewardModal}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>{editingReward ? "Edit Reward" : "Create Reward"}</DialogTitle>
            </DialogHeader>
            <div className="grid grid-cols-2 gap-4 py-4">
              <div>
                <Label>Title</Label>
                <Input value={rewardForm.title} onChange={(e) => setRewardForm({...rewardForm, title: e.target.value})} />
              </div>
              <div>
                <Label>Icon (Emoji)</Label>
                <Input value={rewardForm.icon} onChange={(e) => setRewardForm({...rewardForm, icon: e.target.value})} />
              </div>
              <div className="col-span-2">
                <Label>Description</Label>
                <Textarea value={rewardForm.description} onChange={(e) => setRewardForm({...rewardForm, description: e.target.value})} />
              </div>
              <div>
                <Label>Reward Type</Label>
                <Select value={rewardForm.reward_type} onValueChange={(value) => setRewardForm({...rewardForm, reward_type: value})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="consultation_discount">Consultation Discount</SelectItem>
                    <SelectItem value="premium_day">Premium Day</SelectItem>
                    <SelectItem value="certificate">Certificate</SelectItem>
                    <SelectItem value="resource_access">Resource Access</SelectItem>
                    <SelectItem value="mentorship">Mentorship</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Value</Label>
                <Input placeholder="e.g., 20% off" value={rewardForm.value} onChange={(e) => setRewardForm({...rewardForm, value: e.target.value})} />
              </div>
              <div>
                <Label>Cost (Points)</Label>
                <Input type="number" value={rewardForm.cost_points} onChange={(e) => setRewardForm({...rewardForm, cost_points: parseInt(e.target.value) || 0})} />
              </div>
              <div>
                <Label>Quantity (leave empty for unlimited)</Label>
                <Input type="number" value={rewardForm.quantity || ""} onChange={(e) => setRewardForm({...rewardForm, quantity: e.target.value ? parseInt(e.target.value) : null})} />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowRewardModal(false)}>Cancel</Button>
              <Button onClick={saveReward}>{editingReward ? "Update" : "Create"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
      <Footer />
    </div>
  );
};

export default AdminGamification;